// WARN: не использовать raw-строки. Все переводы идут через i18n.
import { isReactive, isRef, toValue } from 'vue'
import { result as _result, get as _get, isObject } from 'lodash-es'
import { commons } from '@/lib/transformer'
import { buildModelLabelsBySection, buildModelValueFormattersBySection } from '@/lib/historyFieldMeta'
import { i18n } from '@/i18n'
import {
  productAfsApiManufacturersFields,
  productAfsApisFields,
  productDosageFormItemsFields,
  productDosageFormsFields,
  productManufacturerGroupLabels,
  productManufacturerGroupFields,
  productManufacturersFields,
  productIndicationsFields,
  productsFields,
} from '@/lib/historyProductsFields'
import { submissionsFields } from '@/lib/historySubmissionsFields'
import { taxesFields } from '@/lib/historyTaxesFields'
import { useConfigsStore } from '@/stores/configs'
import { productDisplayName } from '@/lib/productCard'
import { formatConfigIdList } from '@/lib/idList'
import {
  catalogDisplayName,
  gmpProductTitle,
  gmpStatusDisplayName,
  localizedRelationTitle,
  siteDisplayName,
  tradeNameOnly,
} from '@/lib/localizedTitle'
import { formatPoLabel, normalizePoEntries } from '@/lib/poEntries'
import {
  historyPhrase,
  historyPo,
  historyKnownLabel,
  historyFormatBool,
  historyFieldLabel,
} from '@/lib/historyPhrases'
import { facsimileHistoryRootLabel } from '@/lib/userFacsimile'

/** Строка заголовка пользователя в истории (в т.ч. JSON `{username,fio}` из SSO). */
function formatUserRelationLabel(rel) {
  if (rel == null || rel === '') return null
  let v = rel
  if (typeof v === 'string') {
    const t = v.trim()
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        const p = JSON.parse(t)
        if (p && typeof p === 'object' && !Array.isArray(p)) v = p
      } catch {
        /* оставляем строку */
      }
    }
  }
  if (isObject(v) && !Array.isArray(v)) {
    const username = (v.username ?? '').toString().trim()
    const fio = historyKnownLabel((v.fio ?? '').toString().trim())
    if (username || fio) return [username, fio].filter(Boolean).join(', ')
    return null
  }
  if (typeof v === 'string' && v.trim()) return v.trim()
  return null
}

function parseRelationRaw(raw) {
  let v = raw
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        const p = JSON.parse(t)
        if (p && typeof p === 'object' && !Array.isArray(p)) v = p
      } catch {
        /* оставляем строку */
      }
    }
  }
  return v
}

/** Определяет язык подписи объекта истории по текущей раскладке LTMS. */
function isEnglishHistoryLocale() {
  return String(toValue(i18n.global.locale) || '').toLowerCase().startsWith('en')
}

/**
 * Имя ТН/ЛП в истории: строка related как есть, объект — по раскладке.
 *
 * @param {unknown} rel
 * @returns {string}
 */
function catalogHistoryName(rel) {
  if (rel == null || rel === '') {
    return ''
  }
  if (typeof rel === 'string' || typeof rel === 'number') {
    return String(rel)
  }
  return catalogDisplayName(rel, isEnglishHistoryLocale())
}

/**
 * Тип подачи для заголовка пошлины. Подписи берутся из config на языке раскладки.
 *
 * @param {object|null|undefined} submission
 * @returns {string}
 */
function submissionTypeLabel(submission) {
  if (!submission || typeof submission !== 'object') {
    return ''
  }
  const raw = submission.submission_type
  if (raw == null || raw === '' || (Array.isArray(raw) && !raw.length)) {
    return ''
  }
  if (Array.isArray(raw) && raw.every(item => item && typeof item === 'object' && item.value)) {
    return raw.map(item => String(item.value || '').trim()).filter(Boolean).join(', ')
  }
  const configStore = useConfigsStore()
  const options = toValue(configStore.get('submissions.submission_type', [])) || []
  return formatConfigIdList(Array.isArray(raw) ? raw : [raw], options)
}

/**
 * Trade name и тип подачи для истории пошлины.
 *
 * @param {unknown} rel
 * @returns {string}
 */
function taxHistoryDetails(rel) {
  if (!isObject(rel)) {
    return ''
  }
  const trade = tradeNameOnly(rel.product, isEnglishHistoryLocale())
  const submissionType = rel.submission ? submissionTypeLabel(rel.submission) : ''
  return [trade, submissionType].filter(Boolean).join(', ')
}

/** Сырое значение поля из changes: value, иначе old_value. */
function historyFieldRaw(ch, field) {
  const change = (ch?.changes || []).find(item => {
    const path = Array.isArray(item?.path) ? item.path : [item?.path]
    return String(path[path.length - 1] || '') === field
  })
  const text = String(change?.value ?? change?.old_value ?? '').trim()
  return text && text !== 'None' ? text : ''
}

/** Текст пункта выпадающего списка по id из related.Dropdowns. */
function dropdownOptionText(related, raw) {
  const id = String(raw ?? '').trim()
  if (!id || id === 'None') return ''
  if (!/^\d+$/.test(id)) return id
  const bucket = related?.Dropdowns
  if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) return ''
  const item = bucket[id] ?? bucket[Number(id)]
  if (typeof item === 'string' || typeof item === 'number') return String(item).trim()
  if (item && typeof item === 'object') return String(item.value || item.name || '').trim()
  return ''
}

/**
 * Текст двуязычного поля в порядке текущей раскладки.
 * Для справочника id заменяется текстом пункта.
 */
function localePairText(ch, related, enField, ruField, dropdown = false) {
  const order = isEnglishHistoryLocale() ? [enField, ruField] : [ruField, enField]
  for (const field of order) {
    const raw = historyFieldRaw(ch, field)
    if (!raw) continue
    const text = dropdown ? dropdownOptionText(related, raw) : (/^\d+$/.test(raw) ? '' : raw)
    if (text) return text
  }
  return ''
}

/** «Дозировка 10 mg» / «Strength 10 mg» — без префикса «Сущность:». */
function typedHistoryEntity(kindKey, value) {
  const kind = historyPhrase(kindKey)
  const text = String(value || '').trim()
  const preview = text.length > 80 ? `${text.slice(0, 80)}…` : text
  return preview ? `${kind} ${preview}` : kind
}

function historyProductSnapshot(related) {
  const bucket = related?.Products
  if (!bucket || typeof bucket !== 'object' || Array.isArray(bucket)) return null
  const rows = Object.values(bucket).filter(item => item && typeof item === 'object')
  return rows[0] || null
}

function historyBlockNumber(map, id) {
  if (!map || typeof map !== 'object' || id == null) return 0
  const n = Number(map[id] ?? map[String(id)])
  return Number.isInteger(n) && n >= 1 ? n : 0
}

function withBlockNumber(label, number) {
  const name = String(label || '').trim()
  if (!name) return number ? `№${number}` : ''
  return number ? `${name} №${number}` : name
}

/** «Dosage form №1: Tablet» — номер блока сразу после названия формы. */
function dosageFormHistoryEntity(ch, related) {
  const product = historyProductSnapshot(related)
  const formN = historyBlockNumber(product?.dosage_form_indexes, ch?.model_id)
  const head = withBlockNumber(historyPhrase('labels.dosageForm'), formN)
  const text = localePairText(ch, related, 'dosage_form_en', 'dosage_form_ru', true)
  const preview = text.length > 80 ? `${text.slice(0, 80)}…` : text
  return preview ? `${head}: ${preview}` : head
}

/** «Dosage form №1: Strength №2» — форма в начале, номер дозировки в конце. */
function dosageItemHistoryEntity(ch, related) {
  const product = historyProductSnapshot(related)
  const formN = historyBlockNumber(product?.strength_form_indexes, ch?.model_id)
  const strengthN = historyBlockNumber(product?.strength_indexes, ch?.model_id)
  const formLabel = withBlockNumber(historyPhrase('labels.dosageForm'), formN)
  const strengthLabel = withBlockNumber(historyPhrase('labels.dosageItem'), strengthN)
  if (formN) return `${formLabel}: ${strengthLabel}`
  return strengthLabel
}

/**
 * Заголовок блока производителя ЛП: то же имя, что на карточке, и номер в группе.
 * RU: «Производитель ГЛФ №1», EN: «FDF manufacturer №1».
 */
function manufacturerHistoryEntityName(ch, related) {
  const product = historyProductSnapshot(related)
  const blocks = product?.manufacturer_blocks
  const meta = blocks && ch?.model_id != null
    ? blocks[ch.model_id] ?? blocks[String(ch.model_id)]
    : null
  const group = ch?.changes?.find(item => item?.group)?.group || meta?.group || 'manufacturer'
  const n = Number(meta?.n)
  const hasNumber = Number.isInteger(n) && n >= 1
  const title = manufacturerBlockLabel(group)
  return hasNumber ? `${title} №${n}` : title
}

/** Название группы площадки на языке карточки, без номера. */
function manufacturerBlockLabel(group) {
  const key = `history.manufacturerBlocks.${group}`
  const locale = String(toValue(i18n.global.locale) || '')
  if (locale && i18n.global.te(key, locale)) {
    const text = i18n.global.t(key)
    if (typeof text === 'string' && text && text !== key) return text
  }
  return productManufacturerGroupLabels[group]?.label
    || productManufacturerGroupLabels.manufacturer?.label
    || 'Manufacturer'
}

/** Подпись повторяемого блока карточки: тип на языке раскладки и текст значения. */
export function repeatingHistoryEntity(ch, related) {
  switch (ch?.model_name) {
    case 'ProductManufacturers':
      return manufacturerHistoryEntityName(ch, related)
    case 'ProductDosageFormItems':
      return dosageItemHistoryEntity(ch, related)
    case 'ProductDosageForms':
      return dosageFormHistoryEntity(ch, related)
    case 'ProductAfsApis':
      return typedHistoryEntity(
        'labels.drugSubstance',
        localePairText(ch, related, 'api_name_en', 'api_name_ru')
      )
    case 'ProductAfsApiManufacturers':
      return typedHistoryEntity(
        'labels.drugSubstanceManufacturer',
        localePairText(ch, related, 'api_manufacturer_name_en', 'api_manufacturer_name_ru')
      )
    case 'ProductIndications':
      return indicationHistoryEntityName(ch, related)
    default:
      return ''
  }
}

////---

/**
 * @callback ChangeFormatter
 * @param {Object} change certain history entry
 * @param {Object} relations all given relations as {RelName:{RelId: Rel}}
 */

/**
 * @callback ChangeValueGetter
 * @param {Object} changeset full changeset (batch)
 * @param {Object} change certain history entry
 * @param {Object|String} relation relation name
 * @param {String} prop what prop in change entry value|old_value
 */

////---

export const models = {
  Dossier: {
    entityName: (ch, rel) => {
      const objects = ch.changes?.[0]?.objects
      const primary =
        (isObject(rel) && rel) ||
        (Array.isArray(objects) && objects[0]) ||
        {}
      const name = escapeHtmlText(primary.name || `#${ch.model_id}`)
      const path = primary.path || ''
      const kind = historyPo(
        primary.kind === 'folder' ? 'dossier_kind_folder' : 'dossier_kind_file'
      )
      // shared.po: dossier_entity / dossier_entity_path (как Excel AT).
      const label = path
        ? historyPo('dossier_entity_path', { kind, name, path: escapeHtmlText(path) })
        : historyPo('dossier_entity', { kind, name })
      // Finder открывает объект по ?link= (см. itemRouteQuery); ?id= он не читает.
      const objectId = primary.id
      const hrefId = objectId != null && objectId !== '' ? objectId : ch.model_id
      if (hrefId == null || hrefId === '' || hrefId === 0) {
        return label
      }
      return `<a href="/finder?link=${encodeURIComponent(hrefId)}">${label}</a>`
    },
  },
  Products: {
    entityName: (ch, rel) => {
      const title = productHistoryTitle(rel) || ch.model_id
      // Всегда ссылка: в Audit Trail и в истории карточки ЛП
      return `${historyPhrase('labels.product')} ${genLink(ch, title, true)}`
    },
    prefix: 'products',
    historyPrefix: 'products',
  },
  Submissions: {
    entityName: (ch, rel, il) => {
      const title = tradeNameOnly(rel?.product, isEnglishHistoryLocale()) || rel?.id || ch.model_id
      return `${historyPhrase('entity.submission')} ${genLink(ch, title, il)}`
    },
    prefix: 'submissions',
    historyPrefix: 'submissions',
  },
  Sites: {
    entityName: (ch, rel, il) => {
      const preferEnglish = isEnglishHistoryLocale()
      if (isObject(rel)) {
        const title = siteDisplayName(rel, preferEnglish)
        if (title) {
          return `${historyPhrase('labels.site')} ${genLink(ch, title, il)}`
        }
      }
      const firstChange = ch.changes?.[0] || {}
      const siteName = siteDisplayName(firstChange, preferEnglish)
      if (siteName) {
        return `${historyPhrase('labels.site')} ${genLink(ch, siteName, il)}`
      }
      return `${historyPhrase('labels.site')} ${genLink(ch, ch.model_id, il)}`
    },
    prefix: 'sites',
    historyPrefix: 'sites',
  },
  GmpStatusList: {
    entityName: (ch, rel, il) => {
      const title = isObject(rel) ? gmpStatusDisplayName(rel, isEnglishHistoryLocale()) : ''
      return `${historyPhrase('entity.gmpStatus')} ${genLink(ch, title || ch.model_id, il)}`
    },
    prefix: 'gmp_status_list',
    historyPrefix: 'gmp_status_list',
  },
  GmpForSubmission: {
    entityName: (ch, rel, il) => {
      const title = isObject(rel) ? gmpProductTitle(rel, isEnglishHistoryLocale()) : ''
      return `GMP for submission ${genLink(ch, title || ch.model_id, il)}`
    },
    prefix: 'gmp_for_submission',
    historyPrefix: 'gmp_for_submission',
  },
  GmpForSubmissionAction: {
    entityName: () => 'Action',
  },
  EaeuProductContacts: {
    entityName: (ch, rel, il) => {
      const title = productDisplayName(rel?.product, isEnglishHistoryLocale())
      return `${historyPhrase('entity.eaeuProductContacts')} ${genLink(ch, title || ch.model_id, il)}`
    },
    prefix: 'eaeu-product-contacts',
    historyPrefix: 'eaeu_product_contacts',
  },
  Taxes: {
    entityName: (ch, rel) => {
      const details = taxHistoryDetails(rel)
      if (details) {
        return historyPhrase('entity.tax', { details })
      }
      const id = isObject(rel) && rel.id != null ? rel.id : ch.model_id
      return historyPhrase('entity.taxId', { id })
    },
    prefix: 'taxes',
    historyPrefix: 'taxes',
  },
  ServiceNotes: {
    entityName: (ch, rel) => {
      const id = isObject(rel) && rel.id != null ? rel.id : ch.model_id
      return historyPhrase('entity.serviceNote', { id })
    },
    prefix: 'documents/service-notes',
    historyPrefix: 'service_notes',
  },
  Templates: {
    entityName: (ch, rel, il) => {
      const title = rel?.name || ch.changes?.[0]?.filename || ch.model_id
      return `${historyPhrase('labels.template')} ${genLink(ch, title, il)}`
    },
    prefix: 'catalog/templates',
    historyPrefix: 'templates',
  },
  MedicineTrademarks: {
    entityName: (_, rel) =>
      historyPhrase('entity.medicineTrademark', { name: catalogHistoryName(rel) }),
    prefix: 'catalog/medicine_trademarks',
  },
  Medicines: {
    entityName: (_, rel) =>
      historyPhrase('entity.medicinalProduct', { name: catalogHistoryName(rel) }),
    prefix: 'catalog/medicines',
  },
  /**
   * Производитель ЛП в catalogs (custom_model в history).
   * related: Manufacturers[id] и/или MedicineManufacturer[medicineId][].
   */
  MedicineManufacturer: {
    entityName: (ch, rel) => {
      let manufacturerName = null
      const manufacturersById = rel?.Manufacturers
      if (manufacturersById) {
        const manufacturer =
          manufacturersById[ch.model_id] ?? manufacturersById[String(ch.model_id)]
        if (manufacturer != null) {
          manufacturerName =
            typeof manufacturer === 'object'
              ? manufacturer.name || null
              : manufacturer
          if (
            typeof manufacturer === 'object' &&
            manufacturer.address &&
            manufacturerName
          ) {
            manufacturerName += `, (${manufacturer.address})`
          }
        }
      }
      if (!manufacturerName && rel?.MedicineManufacturer) {
        for (const medicineId of Object.keys(rel.MedicineManufacturer)) {
          const manufacturers = rel.MedicineManufacturer[medicineId]
          if (!Array.isArray(manufacturers)) continue
          const manufacturer = manufacturers.find(
            m => String(m?.id) === String(ch.model_id)
          )
          if (!manufacturer) continue
          if (manufacturer.name) {
            manufacturerName = manufacturer.name
          }
          if (manufacturer.address && manufacturerName) {
            manufacturerName += `, (${manufacturer.address})`
          }
          break
        }
      }
      return manufacturerName
        ? historyPhrase('entity.manufacturerNamed', { name: manufacturerName })
        : historyPhrase('entity.manufacturerId', { id: ch.model_id })
    },
  },
  User: {
    entityName: (ch, rel, il) => {
      const label = formatUserRelationLabel(rel) ?? ch.model_id
      return `${historyPhrase('entity.user')} ${genLink(ch, label, il)}`
    },
    prefix: 'users',
  },
  Uploads: {
    entityName: ch => {
      let l = [historyPhrase('labels.document') || 'Document']
      const div = ch.changes[0]?.division
      const fname = ch.changes[0]?.filename
      if (div) {
        l.push(`«${entityDocumentDivisionLabel(div)}»`)
      }
      if (fname) {
        l.push(fname)
      }
      return l.join(' ')
    },
  },
  Comments: {
    entityName: (ch, _rel, il) => {
      const histKey = ch.hist_key || ''
      const parts = histKey.split(':')
      if (parts.length >= 2) {
        const parentModel = parts[0]
        const parentId = parts[1] !== 'None' ? parts[1] : ''
        const parentConfig = models[parentModel]
        const labels = {
            Products: historyPhrase('labels.product'),
            Submissions: historyPhrase('labels.submission'),
        }
        const label = labels[parentModel] || historyPhrase('entity.comment')
        if (parentConfig?.prefix && parentId && il) {
          return `${label} <a href="/${parentConfig.prefix}/${parentId}">${parentId}</a>`
        }
        if (parentId) {
          return `${label} ${parentId}`
        }
        return label
      }
      return historyPhrase('entity.comment')
    },
  },
  ProductManufacturers: {
    /**
     * «Производитель ГЛФ №1» / «FDF manufacturer №1» — номер внутри группы.
     * @param {Object} ch История изменений
     * @param {Object} rel related истории, со снимком продукта
     */
    entityName: (ch, rel) => manufacturerHistoryEntityName(ch, rel),
    /**
     * Возвращает название поля с учетом группы блока (префикс или специальный label)
     * @param {Object} ch История изменений
     * @param {Object} change Конкретное изменение в пачке
     * @param {String|Object} defaultLabel Стандартное название поля из словаря
     */
    fieldName: (ch, change, defaultLabel) => {
      const g = change.group || ch.changes?.find(c => c.group)?.group
      const fieldPath = Array.isArray(change.path)
        ? change.path[change.path.length - 1]
        : change.path

      // site/site_id — тот же дропдаун, что «Batch release name»: в истории текст площадки.
      if ((fieldPath === 'site' || fieldPath === 'site_id') && g) {
        const nameLabel = productManufacturerGroupFields[g]?.name_eng?.label || 'Site'
        return historyFieldLabel(ch.model_name, `${g}.name_eng`, nameLabel)
      }

      const resolvedDefault =
        typeof defaultLabel === 'string'
          ? defaultLabel
          : defaultLabel && typeof defaultLabel === 'object'
            ? defaultLabel.label || defaultLabel.title || null
            : null

      if (!g) {
        const fallback =
          resolvedDefault ||
          productManufacturersFields[fieldPath]?.label ||
          fieldPath
        return historyFieldLabel(ch.model_name, fieldPath, fallback) || fieldPath
      }

      const groupLabel = productManufacturerGroupFields[g]?.[fieldPath]?.label
      if (groupLabel) {
        // name_rus и соседние поля общие для всех групп, а подписи разные.
        // Плоский history.fields.ProductManufacturers.name_rus список истории
        // наложил бы на каждую группу.
        return historyFieldLabel(ch.model_name, `${g}.${fieldPath}`, groupLabel)
      }

      const base = historyFieldLabel(
        ch.model_name,
        fieldPath,
        resolvedDefault || productManufacturersFields[fieldPath]?.label || null
      )

      const label =
        historyFieldLabel(
          'ProductManufacturers',
          g,
          productManufacturerGroupLabels[g]?.label || 'Manufacturer'
        ) || 'Manufacturer'

      if (base == null || base === '' || base === 'undefined' || base === label) {
        return label
      }
      return `${label} - ${base}`
    },
  },
  ProductDosageForms: {
    entityName: (ch, rel) => repeatingHistoryEntity(ch, rel),
  },
  ProductDosageFormItems: {
    entityName: (ch, rel) => repeatingHistoryEntity(ch, rel),
  },
  ProductAfsApis: {
    entityName: (ch, rel) => repeatingHistoryEntity(ch, rel),
  },
  ProductAfsApiManufacturers: {
    entityName: (ch, rel) => repeatingHistoryEntity(ch, rel),
  },
  ProductIndications: {
    entityName: (ch, rel) => indicationHistoryEntityName(ch, rel),
  },
  Dropdowns: {
    entityName: (ch, rel) => {
      const changes = ch?.changes || []
      const refFromBackend = changes.map(c => c?.dropdown_ref).find(Boolean)
      const c0 = changes[0] || {}
      let field = refFromBackend || c0.field || ''
      if (
          !field &&
          Array.isArray(c0.path) &&
          c0.path.length &&
          !/^(key|value)$/i.test(c0.path.join('.'))
      ) {
        field = c0.path.join('.')
      }
      // Локализация namespace и полей dropdowns — отдельная задача.
      // Пока показываем технический путь dropdown_ref как есть.
      const technicalPath = field.split('.').filter(Boolean).join(' / ')
      const ddVal =
          changes.map(c => c?.dd_value).find(v => v != null && v !== '') ?? c0.dd_value
      const ddKey =
          changes.map(c => c?.dd_key).find(v => v != null && v !== '') ?? c0.dd_key
      // Колонка «Объект»: человекочитаемое значение дропдауна; если нет — ключ (старые записи)
      const valueShown =
          ddVal != null && ddVal !== ''
              ? String(ddVal)
              : rel != null && rel !== ''
                  ? String(rel)
                  : ddKey != null && ddKey !== ''
                      ? String(ddKey)
                      : ''
      const bits = [historyPhrase('labels.dropdownList')]
      if (technicalPath) bits.push(technicalPath)
      if (valueShown) bits.push(valueShown)
      return bits.join(' ')
    },
  },
  Rsd: {
    entityName: (ch, rel, il) => {
      const title =
        (isObject(rel) && (rel.title || rel.trade_name_en || rel.trade_name_ru) && (rel.title || rel.trade_name_en || rel.trade_name_ru)) ||
        ch.model_id
      return `RSD ${genLink(ch, title, il)}`
    },
    prefix: 'rsd',
    historyPrefix: 'rsd',
    fieldName: (ch, change, defaultLabel) => {
      const fieldPath = Array.isArray(change.path)
        ? change.path.filter(segment => !/^\d+$/.test(String(segment)))
        : change.path
      const resolvedDefault =
        typeof defaultLabel === 'string'
          ? defaultLabel
          : defaultLabel && typeof defaultLabel === 'object'
            ? defaultLabel.label || null
            : null
      if (Array.isArray(fieldPath) && fieldPath[0] === 'patents' && fieldPath[1]) {
        const key = RSD_PATENT_FIELD_LABEL_KEYS[fieldPath[1]]
        return key ? historyPhrase(key) : resolvedDefault
      }
      if (Array.isArray(fieldPath) && fieldPath.length) {
        const fullKey = fieldPath.join('.')
        const labeled = historyFieldLabel(ch.model_name, fullKey, null)
        if (labeled) return labeled
      }
      return resolvedDefault
    },
  },
  // Legacy catalog dropdown model отключен в LTMS.
}

function productHistoryTitle(rel) {
  if (rel == null || rel === '') {
    return null
  }
  if (typeof rel === 'string' || typeof rel === 'number') {
    const text = String(rel).trim()
    // Чистый id («1») — не торговое наименование
    if (!text || /^\d+$/.test(text)) {
      return null
    }
    return text
  }
  if (!isObject(rel) || Array.isArray(rel)) {
    return null
  }
  const title = productDisplayName(rel, isEnglishHistoryLocale())
  return title || null
}

function genLink(ch, txt, il, linkId) {
  const model = models[ch.model_name]
  const id = linkId !== undefined ? linkId : ch.model_id
  return il
      ? model
          ? typeof model.reflink === 'function'
              ? model.reflink(ch, txt)
              : `<a href="/${model.prefix}/${id}">${txt}</a>`
          : txt
      : txt
}

export function formatRelationHistoryValue(value) {
  const v = parseRelationRaw(value)
  if (v == null || typeof v !== 'object' || Array.isArray(v)) {
    return v
  }
  const username = (v.username ?? '').toString().trim()
  const fio = historyKnownLabel((v.fio ?? '').toString().trim())
  if (username || fio) {
    return [username, fio].filter(Boolean).join(', ')
  }
  const localized = localizedRelationTitle(v, isEnglishHistoryLocale())
  if (localized) {
    return localized
  }
  const caption = historyKnownLabel(String(v.caption ?? v.name ?? v.value ?? ''))
  const service = String(v.service ?? '').trim().toUpperCase()
  if (caption && service) {
    return historyPhrase('relation.withModule', { caption, service })
  }
  const preferEn = isEnglishHistoryLocale()
  const siteName = preferEn
    ? v.ml_site_name_en || v.ml_site_name_ru || v.name_eng || v.name_rus
    : v.ml_site_name_ru || v.ml_site_name_en || v.name_rus || v.name_eng
  const tradeName = preferEn
    ? v.trade_name_en || v.trade_name_ru
    : v.trade_name_ru || v.trade_name_en
  return (
    caption ||
    (siteName && String(siteName).trim()) ||
    tradeName ||
    v.fio ||
    v.username ||
    v.title ||
    v.inn_en ||
    v.inn_ru ||
    (v.id != null && v.id !== '' ? String(v.id) : '') ||
    ''
  )
}

const HISTORY_NOTIFICATION_SERVICE_LABELS = {
  qems: 'QEMS',
  qdms: 'QDMS',
  rtms: 'RTMS',
  ltms: 'LTMS',
  catalogs: 'Catalogs',
  sso: 'SSO',
}

/**
 * Человекочитаемая подпись NotificationSettings для истории User.
 * @param {unknown} raw
 * @returns {string}
 */
export function formatNotificationSettingHistoryLabel(raw) {
  if (raw == null || raw === '') {
    return ''
  }
  let v = raw
  if (typeof v === 'string') {
    const t = v.trim()
    if (t.startsWith('{') && t.endsWith('}')) {
      try {
        const p = JSON.parse(t)
        if (p && typeof p === 'object' && !Array.isArray(p)) {
          v = p
        }
      } catch {
        /* оставляем строку */
      }
    }
  }
  if (isObject(v) && !Array.isArray(v)) {
    const caption = historyKnownLabel(String(v.caption ?? v.name ?? v.value ?? '').trim())
    const serviceKey = String(v.service ?? '').trim().toLowerCase()
    const serviceLabel =
      HISTORY_NOTIFICATION_SERVICE_LABELS[serviceKey] ||
      (serviceKey ? serviceKey.toUpperCase() : '')
    if (caption && serviceLabel) {
      return historyPhrase('relation.withModule', { caption, service: serviceLabel })
    }
    if (caption) {
      return caption
    }
  }
  const s = String(v).trim()
  return s && s !== 'undefined' && s !== 'null' ? s : ''
}

function valueNotificationSetting(_, change, rel, prop) {
  const raw = change[prop]
  const fromRelated = lookupHistoryRelated(rel, change.relation || 'NotificationSettings', raw)
  return (
    formatNotificationSettingHistoryLabel(fromRelated) ||
    formatNotificationSettingHistoryLabel(raw) ||
    valueRelation(_, change, rel, prop)
  )
}

function lookupHistoryRelated(rel, relName, rawId) {
  if (!rel || relName == null || rawId == null || rawId === '') {
    return null
  }
  const bucket = rel[relName]
  if (!bucket) {
    return null
  }
  const id = normalizeRelationId(rawId)
  if (typeof bucket === 'object' && !Array.isArray(bucket)) {
    return bucket[id] ?? bucket[String(id)] ?? null
  }
  if (Array.isArray(bucket)) {
    return bucket.find(item => String(item?.id) === String(id)) ?? null
  }
  return null
}

function valueRelation(_, change, rel, prop) {
  const raw = change[prop]
  const resolved =
    lookupHistoryRelated(rel, change.relation, raw) ??
    _result(rel, [change.relation, raw], raw)
  return formatRelationHistoryValue(resolved ?? raw)
}

/** Relation с запасным резолвом через config (например этапы SMOH). */
function valueRelationConfig(configPath) {
  return (ch, change, rel, prop) => {
    const raw = change[prop]
    const fromRel = valueRelation(ch, change, rel, prop)
    const id = normalizeRelationId(raw)
    if (
      fromRel != null &&
      fromRel !== '' &&
      String(fromRel) !== String(raw) &&
      String(fromRel) !== String(id)
    ) {
      return fromRel
    }
    const configStore = useConfigsStore()
    const label = toValue(configStore.getById(configPath, id, null))
    return label || fromRel || raw
  }
}

/**
 * Подпись dropdown id: EN-справочник по id, RU — по тому же key.
 *
 * @param {string} enPath
 * @param {string} ruPath
 * @param {unknown} id
 * @returns {string|null}
 */
function localizedConfigLabelById(enPath, ruPath, id) {
  const configStore = useConfigsStore()
  const enOpts = toValue(configStore.get(enPath, [])) || []
  const enItem = enOpts.find(item => item.id == id)
  if (!enItem) {
    const fallback = toValue(configStore.getById(enPath, id, null))
    return fallback == null ? null : String(fallback)
  }
  if (isEnglishHistoryLocale()) {
    return enItem.value || null
  }
  const ruOpts = toValue(configStore.get(ruPath, [])) || []
  const ruItem = ruOpts.find(item => item.key === enItem.key)
  return ruItem?.value || enItem.value || null
}

/** Как valueRelationConfig, но с RU-подписями при русской раскладке. */
function valueRelationConfigLocalized(enPath, ruPath) {
  return (ch, change, rel, prop) => {
    const raw = change[prop]
    const fromRel = valueRelation(ch, change, rel, prop)
    const id = normalizeRelationId(raw)
    if (
      fromRel != null &&
      fromRel !== '' &&
      String(fromRel) !== String(raw) &&
      String(fromRel) !== String(id)
    ) {
      return fromRel
    }
    return localizedConfigLabelById(enPath, ruPath, id) || fromRel || raw
  }
}

/**
 * Этап подачи в истории хранится как id Dropdowns (235), а переводы — по key
 * (`history.stages.planning`). Без key vue-i18n показывает сам путь ключа.
 */
function submissionStageLabel(raw) {
  const id = normalizeRelationId(raw)
  let stageKey = null
  if (raw && typeof raw === 'object' && raw.key) {
    stageKey = String(raw.key)
  } else if (typeof raw === 'string' && raw && !/^\d+$/.test(raw.trim())) {
    stageKey = raw.trim()
  }
  const configStore = useConfigsStore()
  const stages = toValue(configStore.get('submissions.stage', [])) || []
  const item = Array.isArray(stages)
    ? stages.find(row => String(row?.id) === String(id) || String(row?.key) === String(stageKey || id))
    : null
  if (item?.key) {
    stageKey = String(item.key)
  }
  if (stageKey) {
    const i18nKey = `history.stages.${stageKey}`
    if (i18n.global.te(i18nKey)) {
      return i18n.global.t(i18nKey)
    }
  }
  return item?.value || (id == null || id === '' ? '' : String(id))
}

function valueAssignUser(_, change, rel, prop) {
  const raw = change[prop]
  const u =
    lookupHistoryRelated(rel, change.relation || 'User', raw) ??
    _result(rel, [change.relation, raw], raw)
  const parsed = parseRelationRaw(u)
  if (isObject(parsed) && !Array.isArray(parsed)) {
    return (parsed.fio || parsed.username || '').toString().trim()
  }
  return parsed ?? ''
}

/** Календарный день для истории: dd.MM.yyyy, без времени и смещения. */
function formatHistoryDay(val) {
  if (val == null || val === '' || val === '0000-00-00') {
    return null
  }
  const s = String(val).trim()
  if (/^[qQ][1-4]\s+\d{4}$/.test(s)) {
    return s
  }
  if (/^\d{2}\.\d{2}\.\d{4}/.test(s)) {
    return s.substring(0, 10)
  }
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) {
    const formatted = commons.dateLocal(s, 'dd.MM.yyyy')
    if (formatted && /^\d{2}\.\d{2}\.\d{4}/.test(String(formatted))) {
      return String(formatted).substring(0, 10)
    }
    return null
  }
  const normalized = s.includes(' ') && !s.includes('T') ? s.replace(' ', 'T') : s
  const formatted = commons.dateLocal(normalized, 'dd.MM.yyyy')
  if (formatted && /^\d{2}\.\d{2}\.\d{4}/.test(String(formatted))) {
    return String(formatted).substring(0, 10)
  }
  return `${match[3]}.${match[2]}.${match[1]}`
}

function valueDate(_, change, __, prop) {
  return formatHistoryDay(change[prop])
}

function valueDateRange(_, change, rel, prop) {
  const raw = parseHistoryJsonValue(change[prop])
  let values = Array.isArray(raw) ? raw : [raw]
  if (!Array.isArray(raw) && typeof raw === 'string') {
    const matches = raw.match(
      /\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?/g,
    )
    if (matches?.length) {
      values = matches
    }
  }
  return values
    .map(value => valueDate(_, { [prop]: value }, rel, prop))
    .filter(Boolean)
    .join('-')
}

/**
 * @type {ChangeValueGetter} valueRegistrationCertificates
 */
function valueRegistrationCertificates(_, change, __, prop) {
  const raw = change[prop]
  if (raw == null || raw === '') return ''
  let list = raw
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw)
    } catch {
      return raw
    }
  }
  if (!Array.isArray(list)) {
    if (list && typeof list === 'object') {
      list = [list]
    } else {
      return String(raw)
    }
  }
  return list
    .map(item => {
      if (!item || typeof item !== 'object') return String(item ?? '')
      const cert = item.certificate || item.registration_certificate || ''
      const dateRaw = item.date || item.registration_at
      const date = dateRaw ? commons.dateLocal(dateRaw, 'dd.MM.yyyy') || String(dateRaw) : ''
      if (cert && date) return `${cert} (${date})`
      return cert || date || ''
    })
    .filter(Boolean)
    .join('; ')
}

function valueBool(_, change, __, prop) {
  return historyFormatBool(change[prop])
}

/** Статус Market Access на карточке: yes/no в языке интерфейса. */
function valueYesNo(_, change, __, prop) {
  const raw = change[prop]
  if (raw == null || raw === '' || raw === 'None' || raw === 'null') return ''
  const text = String(raw).trim().toLowerCase()
  if (['yes', 'true', '1', 'да'].includes(text)) return historyPhrase('homeCard.yes')
  if (['no', 'false', '0', 'нет'].includes(text)) return historyPhrase('homeCard.no')
  return String(raw).trim()
}

/** Заявки RTMS в истории подачи: «№123 (Статус), …». */
function valueRtmsApplications(_, change, __, prop) {
  const list = parseHistoryJsonValue(change[prop])
  if (!Array.isArray(list) || !list.length) {
    return '—'
  }
  return list
    .map(item => {
      if (!item || typeof item !== 'object') return String(item ?? '')
      const number = item.number || item.id || '—'
      const status = item.status ? ` (${item.status})` : ''
      return `№${number}${status}`
    })
    .filter(Boolean)
    .join(', ')
}

const RSD_REGISTRATION_TYPE_LABEL_KEYS = {
  new_registration: 'rsd.registrationType.new_registration',
  registration_extension: 'rsd.registrationType.registration_extension',
}

const RSD_REGISTRATION_PROCEDURE_LABEL_KEYS = {
  national: 'rsd.registrationProcedure.national',
  eaeu_mrp: 'rsd.registrationProcedure.eaeu_mrp',
  eaeu_dcp: 'rsd.registrationProcedure.eaeu_dcp',
}

const RSD_POST_REGISTRATION_MEASURE_TYPE_LABEL_KEYS = {
  additional_requirements: 'rsd.postRegistrationMeasureType.additional_requirements',
  conditional_registration: 'rsd.postRegistrationMeasureType.conditional_registration',
  accelerated_registration: 'rsd.postRegistrationMeasureType.accelerated_registration',
  exceptional_registration: 'rsd.postRegistrationMeasureType.exceptional_registration',
}

const RSD_APPLICABLE_LABEL_KEYS = {
  applicable: 'rsd.applicable.applicable',
  not_applicable: 'rsd.applicable.not_applicable',
}

const RSD_PATENT_FIELD_LABEL_KEYS = {
  number: 'rsd.patent.number',
  expiry_date: 'rsd.patent.expiryDate',
}

function valueRsdEnum(labelKeys) {
  return (_, change, __, prop) => {
    const key = labelKeys[change[prop]]
    return key ? historyPhrase(key) : (change[prop] || '')
  }
}

function parseHistoryJsonValue(raw) {
  if (raw == null || raw === '') {
    return null
  }
  if (typeof raw === 'object') {
    return raw
  }
  if (typeof raw !== 'string') {
    return raw
  }
  try {
    return JSON.parse(raw)
  } catch {
    try {
      return JSON.parse(raw.replace(/'/g, '"'))
    } catch {
      return raw
    }
  }
}

function valueRsdPatent(_, change, __, prop) {
  const parsed = parseHistoryJsonValue(change[prop])
  if (Array.isArray(parsed)) {
    return parsed
      .map(item =>
        valueRsdPatent(_, { [prop]: item }, __, prop)
      )
      .filter(Boolean)
      .join('; ') || '—'
  }
  if (!parsed || typeof parsed !== 'object') {
    if (parsed == null || parsed === '') {
      return '—'
    }
    return String(parsed)
  }
  const number =
    parsed.number != null && String(parsed.number).trim() !== ''
      ? String(parsed.number).trim()
      : ''
  const expiry = parsed.expiry_date
    ? valueDate(_, { [prop]: parsed.expiry_date }, __, prop)
    : ''
  // Всегда человекочитаемо: пустые поля как «—», без сырого JSON в деталях.
  return [
    `${historyPhrase('rsd.patent.valueNumber')}: ${number || '—'}`,
    `${historyPhrase('rsd.patent.valueExpiry')}: ${expiry || '—'}`,
  ].join('; ')
}

// -----------------------------
// Products* field meta section
// -----------------------------
const PRODUCT_MODEL_FIELDS = {
  Products: {
    modelPath: 'products',
    fields: productsFields,
  },
  ProductManufacturers: {
    modelPath: 'product_manufacturers',
    fields: productManufacturersFields,
  },
  ProductDosageForms: {
    modelPath: 'product_dosage_forms',
    fields: productDosageFormsFields,
  },
  ProductDosageFormItems: {
    modelPath: 'product_dosage_form_items',
    fields: productDosageFormItemsFields,
  },
  ProductAfsApis: {
    modelPath: 'product_afs_apis',
    fields: productAfsApisFields,
  },
  ProductAfsApiManufacturers: {
    modelPath: 'product_afs_api_manufacturers',
    fields: productAfsApiManufacturersFields,
  },
  ProductIndications: {
    modelPath: 'product_indications',
    fields: productIndicationsFields,
  },
}

const productModelLabels = buildModelLabelsBySection(PRODUCT_MODEL_FIELDS)

// -----------------------------
// Submissions field meta section
// -----------------------------
const SUBMISSION_MODEL_FIELDS = {
  Submissions: {
    modelPath: 'submissions',
    fields: submissionsFields,
  },
}

const submissionModelLabels = buildModelLabelsBySection(SUBMISSION_MODEL_FIELDS)

const TAX_MODEL_FIELDS = {
  Taxes: {
    modelPath: 'taxes',
    fields: taxesFields,
  },
}

const taxesModelLabels = buildModelLabelsBySection(TAX_MODEL_FIELDS)

/** Корни факсимиле User — подпись только через facsimileHistoryRootLabel (текущая раскладка). */
const USER_FACSIMILE_ROOT_KEYS = new Set(['facsimile_regspec', 'facsimile_reglead'])

const USER_FACSIMILE_FIELD_KEYS = new Set([
  'facsimile_regspec',
  'facsimile_reglead',
  'facsimile_commerce',
  'facsimile_coordinator',
])

function normalizeHistoryPathSegments(path) {
  if (Array.isArray(path)) {
    return path.map(segment => String(segment))
  }
  if (path == null || path === '') {
    return []
  }
  if (typeof path === 'string') {
    return path.split('.').filter(Boolean)
  }
  return [String(path)]
}

/** Сырой data URL, JSON signImage или длинная base64-строка из истории факсимиле. */
export function isFacsimileBinaryHistoryValue(raw) {
  if (raw == null || raw === '') {
    return false
  }
  const s = String(raw).trim()
  if (!s) {
    return false
  }
  if (s.startsWith('data:image') || (s.includes('data:image') && s.includes('base64'))) {
    return true
  }
  if (s.startsWith('{') && s.includes('dataUrl')) {
    try {
      const obj = JSON.parse(s)
      if (obj && typeof obj === 'object' && obj.dataUrl != null) {
        return true
      }
    } catch {
      return s.includes('base64')
    }
  }
  const compact = s.replace(/\s/g, '')
  if (compact.length > 300 && /^[A-Za-z0-9+/=]+$/.test(compact.slice(0, 512))) {
    return true
  }
  return false
}

export function isFacsimileBinaryHistoryText(html) {
  if (!html) {
    return false
  }
  const plain = String(html).replace(/<[^>]+>/g, '').trim()
  if (isFacsimileBinaryHistoryValue(plain)) {
    return true
  }
  if (plain.includes('"dataUrl"') && plain.includes('base64')) {
    return true
  }
  return false
}

/**
 * DeepDiff по JSON факсимиле пишет отдельные записи для signImage.dataUrl (base64).
 * Их не показываем: достаточно изменения корневого facsimile_* или «скан подписи».
 * @param {string} modelName
 * @param {{ path?: string[]|string, action?: string, value?: unknown, old_value?: unknown }} change
 */
export function shouldSkipUserFacsimileHistoryChange(modelName, change) {
  if (modelName !== 'User') {
    return false
  }
  const parts = normalizeHistoryPathSegments(change?.path)
  if (!parts.length || !USER_FACSIMILE_FIELD_KEYS.has(parts[0])) {
    return false
  }

  if (parts.includes('signImageLayout')) {
    return true
  }

  const signImageIdx = parts.indexOf('signImage')
  if (signImageIdx >= 0) {
    return true
  }

  for (const key of ['value', 'old_value']) {
    if (isFacsimileBinaryHistoryValue(change?.[key])) {
      return true
    }
  }

  return false
}

/**
 * @type {ChangeValueGetter}
 */
function valueFacsimileSignImage(_, change, __, prop = 'value') {
  const raw = change?.[prop]
  if (raw == null || raw === '' || raw === 'None' || raw === 'null') {
    return ''
  }
  if (isFacsimileBinaryHistoryValue(raw)) {
    return historyPhrase('facsimile.signatureImage').toLocaleLowerCase()
  }
  let obj = raw
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw)
    } catch {
      return ''
    }
  }
  if (obj && typeof obj === 'object' && (obj.dataUrl || obj.signImage?.dataUrl)) {
    return historyPhrase('facsimile.signatureImage').toLocaleLowerCase()
  }
  return ''
}

const USER_FACSIMILE_SUB_LABEL_KEYS = {
  jobTitle: 'facsimile.jobTitle',
  signView: 'facsimile.fullName',
  dateLabel: 'facsimile.signatureDate',
  signImage: 'facsimile.signatureImage',
  signImageLayout: 'facsimile.imagePosition',
}

function userFacsimileNestedHistoryLabel(rootKey, subKey) {
  if (!USER_FACSIMILE_ROOT_KEYS.has(rootKey)) return null
  const rootLabel = facsimileHistoryRootLabel(rootKey)
  if (!rootLabel) return null
  const key = USER_FACSIMILE_SUB_LABEL_KEYS[subKey]
  const sub = key ? historyPhrase(key) : subKey
  return `"${rootLabel}": "${sub}"`
}

/**
 * Подпись поля User в истории (в т.ч. вложенные facsimile_* из DeepDiff).
 * @param {string[]|string} path
 * @returns {string|null}
 */
export function userHistoryFieldLabel(path) {
  let parts = Array.isArray(path) ? [...path] : path != null ? [path] : []
  if (parts.length === 1 && String(parts[0]).includes('.')) {
    parts = String(parts[0]).split('.').filter(Boolean)
  }
  if (!parts.length) return null

  const root = parts[0]
  if (!USER_FACSIMILE_ROOT_KEYS.has(root)) return null
  const rootLabel = facsimileHistoryRootLabel(root)
  if (!rootLabel) return null

  if (parts.length === 1) return `"${rootLabel}"`
  return userFacsimileNestedHistoryLabel(root, parts[1])
}

/**
 * @type {ChangeValueGetter} valueSignature
 */
function valueSignature(_, change, __, prop = 'value') {
  let raw =
    change?.[prop] ??
    (prop === 'value' || prop === 'new_value'
      ? (change?.value ?? change?.new_value)
      : (change?.old_value ?? change?.previous_value))
  if (raw == null || raw === '' || raw === 'None' || raw === 'null') {
    return ''
  }
  let obj = raw
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw)
    } catch {
      obj = null
    }
  }
  if (!obj || typeof obj !== 'object') {
    return ''
  }

  const fio = String(obj.signView || '').trim()
  const job = String(obj.jobTitle || '').trim()
  const date = String(obj.dateLabel || '').trim()

  const parts = []
  if (fio) parts.push(fio)
  if (job) parts.push(job)
  if (date) parts.push(date)
  if (obj.signImage?.dataUrl || (obj.dataUrl && String(obj.dataUrl).includes('base64'))) {
    parts.push(historyPhrase('facsimile.signatureImage').toLocaleLowerCase())
  }

  if (parts.length) {
    return parts.join(', ')
  }

  if (obj.image && (obj.image.dataUrl || obj.image.w || obj.image.h)) {
    return historyPhrase('facsimile.signatureAsImage').toLocaleLowerCase()
  }
  return historyPhrase('facsimile.signature').toLocaleLowerCase()
}

// WARN: русские строковые label ниже — fallback истории. В модалке EN/RU
// подставляется из `history.fields.<Model>.<field>`, затем
// `history.fields.common.<field>`; при правке добавляйте обе локали.
// Базовый словарь подписей полей для истории изменений:
// - плоские ключи применяются по умолчанию для всех моделей;
// - вложенные секции (`Products`, `Submissions`, …) переопределяют подписи в рамках конкретной модели.
const fieldLabels = {
  comment: 'Комментарий',
  deleted: 'Удалено',
  description: 'Описание',
  name: 'Название',
  name_en: 'Наименование на английском',
  name_short: 'Сокращение',
  phone: 'Телефон',
  roles: 'Роли',
  status: 'Статус',
  status_text: 'Статус',
  notification_settings: 'Настройки получаемых сообщений',
  ...productModelLabels.Products,
  ...productModelLabels.ProductManufacturers,
  ...productModelLabels.ProductDosageForms,
  ...productModelLabels.ProductDosageFormItems,
  ...productModelLabels.ProductAfsApis,
  ...productModelLabels.ProductAfsApiManufacturers,
  ...productModelLabels.ProductIndications,
  ...submissionModelLabels.Submissions,
  Products: {
    ...productModelLabels.Products,
    // WARN: строки ниже — fallback; EN/RU из history.fields.Products.*
    restricted_access: 'Access restriction',
    dosage_forms: 'Dosage form',
    manufacturers: 'Производители ЛП',
    afs_apis: 'АФС',
    uploads: 'Нормативные документы',
    comments: 'Комментарии',
    doc_num: 'Номер документа',
    doc_date: 'Дата документа',
  },
  Sites: {
    date_created: 'Дата создания',
    ml_site_name_ru: 'ML Site name in Russian',
    ml_site_name_en: 'ML Site name in English',
    ml_address_site_name_en: 'ML address site name in English',
    ml_address_site_name_ru: 'ML address site name in Russian',
    alternative_name: 'Alternative name',
    gmp_rf_eaeu_site_name_ru: 'GMP RF/EAEU site name Russian',
    gmp_rf_eaeu_site_name_en: 'GMP RF/EAEU site name English',
    eaeu_rf_eaeu_address_ru: 'GMP RF/EAEU address Russian',
    eaeu_rf_eaeu_address_en: 'GMP RF/EAEU address English',
    license_expiry_date: 'Срок действия лицензии',
    manufacturer_country_certificate_expiry_date:
      'The validity period of the manufacturer country certificate',
    certificate_expiry_date: 'Срок действия сертификата ЕАЭС',
    certificate_number: 'Номер документа сертификата ЕАЭС',
    the_number_of_gmp_certificate: 'The number of GMP certificate',
    doc_num: 'Номер документа',
    doc_date: 'Дата документа',
    uploads: 'Документы',
  },
  GmpStatusList: {
    date_created: 'Дата создания',
    site: 'site name',
    status: 'Status',
    status_certificate: 'Status certificate',
    eaeu_country: 'EAEU country',
    date_of_documents_request: 'Date of documents request',
    planned_submission_date: 'Planned submission date',
    approval_of_submission_by_global_team: 'Approval of submission by Global team',
    actual_submission_date: 'Actual submission date',
    products: 'Products',
    strategy: 'Strategy',
    internal_decision: 'Internal decision',
    weekly_status_update_comments: 'Weekly status update (comments)',
    ha_notification: 'HA notification',
    planned_inspection_date: 'Planned inspection date',
    inspections_dates: 'Inspections dates',
    inspection_report: 'inspection report',
    gmp: 'GMP issue date',
    eaeu_rf_eaeu_address_en: 'GMP RF/EAEU address English',
    existing_gmp_certificate_expiring_date: 'Existing GMP certificate expiring date',
    the_number_of_gmp_certificate: 'The number of GMP certificate',
    restrictions: 'Restrictions',
    covered_dosage_form_stage: 'Covered dosage form/stage',
    certificate: 'EAEU certificate',
    uploads: 'Сертификат',
  },
  GmpForSubmission: {
    date_created: 'Дата создания',
    product: 'Product',
    product_manual: 'Manual Product input',
    product_manual_value: 'Product',
    bu: 'BU',
    manual_bu: 'BU',
    ra_sub_plan_in_ru: 'RA Sub plan in RU',
    bdp_site: 'BDP site',
    bdp_site_manual: 'Manual BDP site input',
    bdp_site_manual_value: 'BDP site',
    bdp_site_gmp_certificate: 'BDP site GMP certificate',
    bdp_site_inspection_date: 'BDP site inspection date',
    batch_release_site: 'Batch release site',
    batch_release_site_manual: 'Manual Batch release site input',
    batch_release_site_manual_value: 'Batch release site',
    br_site_gmp_certificate: 'BR site GMP certificate',
    br_site_inspection_date: 'BR site inspection date',
    actions: 'Actions',
    comments: 'Comments',
    risk_assessment: 'Risk assessment',
  },
  GmpForSubmissionAction: {
    date_created: 'Дата создания',
    sort: 'Положение в Actions',
    user: 'User',
    date: 'Date',
    text: 'Text',
    comments: 'Comments',
    risk_assessment: 'Risk assessment',
  },
  EaeuProductContacts: {
    date_created: 'Дата создания',
    country: 'Country',
    bu: 'BU',
    product: 'Product',
    inn: 'INN',
    ra_cmc: 'RA CMC',
    ra_cmc_user: 'RA CMC',
    ra_cmc_manual: 'Manual RA CMC input',
    ra_cmc_manual_value: 'RA CMC',
    ra_pil: 'RA PIL',
    ra_pil_user: 'RA PIL',
    ra_pil_manual: 'Manual RA PIL input',
    ra_pil_manual_value: 'RA PIL',
    ra_lead: 'RA Lead',
    ra_lead_user: 'RA Lead',
    ra_lead_manual: 'Manual RA Lead input',
    ra_lead_manual_value: 'RA Lead',
    medical_lead: 'Medical Lead',
    medical_lead_user: 'Medical Lead',
    medical_lead_manual: 'Manual Medical Lead input',
    medical_lead_manual_value: 'Medical Lead',
  },
  Medicines: {
    product_form: 'Форма выпуска',
    prod_stage: 'Этап производства',
    registration_certificate: '№ Регистрационного удостоверения',
    registration_certificates: 'Регистрационные удостоверения',
    description: 'Описание',
    dosage: 'Дозировка',
    medicine_trademark: 'Торговое наименование',
    ru_owner: 'Владелец РУ',
    registration_at: 'Дата регистрации',
    storage_conditions: 'Условия хранения',
    ru_reissue_date: 'Дата переоформления РУ',
  },
  MedicineManufacturer: {
    prod_stage: 'Этап производства',
  },
  MedicineTrademarks: {
    name: 'Наименование',
    code: 'Код продукта',
    medicinal_form: 'Лекарственная форма',
    mhh: 'MHH',
    vortex_site: 'Vortex site',
    name_eng: 'Наименование на английском',
  },
  Taxes: {
    date_created: 'Дата создания',
    ...taxesModelLabels.Taxes,
    doc_num: 'Номер документа',
    doc_date: 'Дата документа',
  },
  ServiceNotes: {
    date_created: 'Дата формирования',
    po_number: '№ РО',
    period_month: 'Месяц периода',
    period_year: 'Год периода',
    period_label: 'Период',
    author: 'Автор',
    total_amount: 'Сумма',
    tax_ids: 'Госпошлины',
    product: 'Product',
    document: 'Service note',
    scan: 'Service note scan',
    service_note: 'Service note scan',
    doc_num: 'Номер документа',
    doc_date: 'Дата документа',
    uploads: 'Документы',
  },
  Templates: {
    name: 'Название',
    template: 'Шаблон',
    doc_num: 'Номер документа',
    doc_date: 'Дата документа',
  },
  ProductManufacturers: {
    ...productModelLabels.ProductManufacturers,
  },
  ProductDosageForms: {
    ...productModelLabels.ProductDosageForms,
    items: 'Strength',
  },
  ProductDosageFormItems: {
    ...productModelLabels.ProductDosageFormItems,
  },
  ProductAfsApis: {
    ...productModelLabels.ProductAfsApis,
    manufacturers: 'АФС manufacturer',
  },
  ProductAfsApiManufacturers: {
    ...productModelLabels.ProductAfsApiManufacturers,
  },
  ProductIndications: {
    ...productModelLabels.ProductIndications,
    text: 'Indication',
    status: 'Indication status',
  },
  Submissions: {
    ...submissionModelLabels.Submissions,
    stage: 'Stage',
  },
  //
  Uploads: {
    metadata: {
      doc_num: 'Номер документа',
      doc_date: 'Дата документа',
      // TODO: старые поля, возможно не будут нужны
      // lab_q_num: '№ запроса из лаборатории',
      // lab_q_date: 'Дата запроса',
      // num_act: 'Номер',
      // date_act: historyPhrase('gmpAction.date'),
      // num_test: 'Номер отчета',
      // date_test: 'Дата отчета',
      // num_wo: 'Номер',
      // date_wo: historyPhrase('gmpAction.date'),
      // num_l_order: 'Номер',
      // date_l_order: historyPhrase('gmpAction.date'),
      // invoice_no: 'Номер счета',
      // invoice_date: 'Дата счета',
      // name: "Тип договора",
      // num: "Номер договора",
      // date: "Дата договора",
      // spec_num: "Номер спецификации",
      // spec_date: "Дата спецификации",
    },
  },
  User: {
    email_notifications: 'E-mail',
    notifications_level: 'Уровень получения сообщений',
    fio: 'ФИО',
    username: 'Логин',
    email: 'PRID',
    data_actual: 'Актуальность данных',
    account_updater_id: 'Ответственный за актуализацию',
    active: 'Статус',
    ltms_global_team: 'Глобальная команда (LTMS)',
    ms_entra: 'OID',
    roles: 'Роли',
    notification_settings: 'Настройки получаемых сообщений',
    // Подписи facsimile_* — userHistoryFieldLabel (текущая раскладка), не строки при импорте.
    facsimile_regspec: {
      signImage: valueFacsimileSignImage,
    },
    facsimile_reglead: {
      signImage: valueFacsimileSignImage,
    },
  },
  Rsd: {
    date_created: 'Дата создания',
    product: 'Product',
    title: 'Наименование',
    registration_type: 'Тип регистрации',
    registration_procedure: 'Процедура регистрации',
    reference_state: 'Референтное государство (РГ)',
    recognition_states: 'Государство-признания (ГП)',
    post_registration_measures: 'Установление пострегистрационных мер',
    post_registration_measures_type: 'Тип пострегистрационных мер',
    additional_requirements_status: 'Регистрация с установлением дополнительных требований',
    conditional_registration_status: 'Условная регистрация',
    accelerated_registration_status: 'Ускоренная регистрация',
    exceptional_registration_status: 'Регистрация в исключительных случаях',
    nosology_code: 'Наличие нозологии в Перечне (код)',
    planned_submission_approval_date: 'Планируемая дата подачи/одобрения',
    conclusion: 'Экспертное заключение',
    conclusion_number: 'Номер заключения',
    conclusion_date: 'Дата заключения',
    regulator_interaction:
      'Взаимодействие с регулятором до подачи досье (официальные письма, предварительные консультации)',
    clinical_data: 'Регуляторные риски: клинические данные',
    gcp: 'GCP',
    gmp_notes: 'GMP',
    samples: 'Образцы',
    other_regulatory: 'Прочие (например, срок годности, торговое наименование)',
    packaging_description: 'Форма выпуска — Описание системы упаковки',
    sku_gtin: 'SKU коды, GTIN',
    mah: 'Держатель РУ',
    manufacturing_sites: 'Производственные площадки',
    global_trade_name: 'Торговое наименование — Глобальное ТН',
    local_trade_name: 'Торговое наименование — Локальное ТН',
    trademark_protection: 'Защита ТЗ при подаче (РГ и ГП)',
    global_team: 'Контакты глобальной команды',
    global_team_irad: 'Контакты глобальной команды: iRAD',
    global_team_iram: 'Контакты глобальной команды: iRAM',
    global_team_icmc: 'Контакты глобальной команды: iCMC',
    global_team_irl_alexion_contacts: 'Контакты глобальной команды IRL (Alexion)',
    world_registration_period: 'Сроки регистрации в мире',
    ref_state_registration_period: 'Сроки регистрации в референтном государстве',
    planned_indication_registration: 'Планируемая редакция показания при регистрации ЛП',
    study_code_name: 'Код исследования, название',
    study_name_phase: 'Название исследования, фаза',
    study_indications: 'Показания (если применимо)',
    early_access_program: 'Программа раннего доступа',
    planned_rf_approval_date: 'Планируемая дата одобрения РФ',
    icsr_ready_date_if_applicable: 'Дата готовности iCSR (если применимо)',
    global_icsr_ready_date: 'Дата готовности iCSR (глобальное КИ)',
    global_fcsr_ready_date: 'Дата готовности fCSR (глобальное КИ)',
    local_fcsr_ready_date: 'Дата готовности fCSR (локальное КИ)',
    planned_registration_dates: 'Планируемые сроки регистрации новых показаний',
    rpc_approval_status: 'Статус согласования с RPC',
    clinical_trial_permission_date: 'Дата получения разрешения ЛКИ',
    icsr_ready_date: 'Дата готовности iCSR',
    fcsr_ready_date: 'Дата готовности fCSR',
    planned_first_global_submission_date: 'Планируемая дата 1-й глобальной подачи',
    planned_us_eu_approval_date: 'Планируемая дата одобрения США, ЕС (если доступна)',
    planned_rf_submission_date: 'Планируемая дата подачи РФ',
    localize_secondary_packaging: 'Локализация вторичной упаковки и ВКК',
    localize_fdf_production: 'Локализация производства ГЛФ',
    localization_planned_dates: 'Планируемые сроки (локализация вторичной упаковки)',
    localization_fdf_planned_dates: 'Планируемые сроки (локализация производства ГЛФ)',
    inclusion_strategy: 'Стратегия по включению препарата в Перечень',
    patents_text: 'Наличие патентов, срок действия',
    circulation_preliminary_testing: 'Предварительное тестирование',
    circulation_preliminary_testing_date: 'Сроки (предварительное тестирование)',
    circulation_launch_strategy: 'Стратегия по вводу в ГО в рамках лонча',
    circulation_launch_strategy_text: 'Стратегия по вводу в ГО в рамках лонча',
    circulation_launch_strategy_date: 'Сроки (стратегия по вводу в ГО)',
    other_information: 'Прочая важная информация',
    patents: 'Патенты',
    comments: 'Комментарий',
    field_comments: 'Комментарий к полю',
    deleted: 'Удалено',
  },
}

// if function, here then no composition
// the function must return a string as hist entry
const modelFieldFormatters = Object.fromEntries(
  Object.entries(models).flatMap(([name, m]) => (m.fields ? [[name, m.fields]] : []))
)

export const fields = {
  ...fieldLabels,
  ...modelFieldFormatters,
}

const historyValueResolvers = {
  relation: valueRelation,
  relationConfig: valueRelationConfig,
  date: valueDate,
  bool: valueBool,
  configIdList: valueConfigIdList,
  poEntries: valuePoEntries,
  packBlocks: valuePackBlocks,
  rtmsApplications: valueRtmsApplications,
}

const productModelValueFormatters = buildModelValueFormattersBySection(
  PRODUCT_MODEL_FIELDS,
  historyValueResolvers
)

const submissionModelValueFormatters = buildModelValueFormattersBySection(
  SUBMISSION_MODEL_FIELDS,
  historyValueResolvers
)

const taxesModelValueFormatters = buildModelValueFormattersBySection(
  TAX_MODEL_FIELDS,
  historyValueResolvers
)

const baseValueFormatters = {
  registration_at: valueDate,
  registration_certificates: valueRegistrationCertificates,
  ru_reissue_date: valueDate,
  medicine_trademark: valueRelation,
  roles: valueRelation,
  notification_settings: valueRelation,
  product_form: valueRelation,
  pathway: valueRelation,
  bu: valueRelation,
  rms: valueRelation,
  status: valueRelation,
  product: valueRelation,
  type_of_product: valueRelation,
  country: valueRelation,
  rc_number_country: valueRelation,
  type_rc_en: valueRelation,
  mah_en: valueRelation,
  mah_ru: valueRelation,
}

export const values = () => ({
  ...baseValueFormatters,
  //
  User: {
    notifications_level: function (_, change, __, prop) {
      const notifications_level = [
        { id: 0, value: historyPhrase('labels.notifLevelNone') },
        { id: 1, value: historyPhrase('labels.notifLevelCritical') },
        { id: 2, value: historyPhrase('labels.notifLevelCriticalNormal') },
        { id: 3, value: historyPhrase('labels.notifLevelAll') },
      ]
      let n = notifications_level.find(d => d.id === Number(change[prop]))
      return n?.value || ''
    },
    data_actual: function (_, change, __, prop) {
      switch (change[prop]) {
        case 'True':
        case true:
        case 1:
        case '1':
          return historyPhrase('labels.dataActual')
        case 'False':
        case false:
        case 0:
        case '0':
          return historyPhrase('labels.dataNotActual')
        default:
          return change[prop] ?? ''
      }
    },
    active: function (_, change, __, prop) {
      switch (change[prop]) {
        case 'True':
        case true:
        case 1:
        case '1':
          return historyPhrase('actions.activated')
        case 'False':
        case false:
        case 0:
        case '0':
          return historyPhrase('actions.deactivated')
        default:
          return change[prop] ?? ''
      }
    },
    account_updater_id: valueAssignUser,
    ltms_global_team: valueBool,
    roles: valueRelation,
    notification_settings: valueNotificationSetting,
    facsimile_regspec: valueSignature,
    facsimile_reglead: valueSignature,
    'facsimile_regspec.signImage': valueFacsimileSignImage,
    'facsimile_reglead.signImage': valueFacsimileSignImage,
  },
  Uploads: {
    metadata: {
      doc_date: valueDate,
      lab_q_date: valueDate,
      date_act: valueDate,
      date_test: valueDate,
      date_wo: valueDate,
      date_l_order: valueDate,
      invoice_date: valueDate,
      date: valueDate,
    },
  },
  Products: {
    ...productModelValueFormatters.Products,
    doc_date: valueDate,
    product_journey: {
      prototype_comment: (_, change, __, prop) => {
        const raw = change[prop]
        if (raw == null || raw === '' || raw === 'None' || raw === 'null') return ''
        return String(raw).trim()
      },
      clinical: {
        submission_plan: valueDate,
        submission_actual: valueDate,
        approval_plan: valueDate,
        approval_actual: valueDate,
      },
      market_access: {
        submission_plan: valueDate,
        submission_actual: valueDate,
        approval_plan: valueDate,
        approval_actual: valueDate,
      },
      release: {
        submission_plan: valueDate,
        submission_actual: valueDate,
        approval_plan: valueDate,
        approval_actual: valueDate,
      },
    },
    clinical_trials: {
      start_plan: valueDate,
      start_actual: valueDate,
      end_plan: valueDate,
      end_actual: valueDate,
    },
    market_access: {
      vednp: { value: valueYesNo, plan_date: valueDate, actual_date: valueDate },
      clinRecs: { value: valueYesNo, plan_date: valueDate, actual_date: valueDate },
    },
    restricted_access: (_, change, __, prop) => {
      const val = change[prop]
      if (val === true || val === 'True' || val === 1 || val === '1' || val === 'Ограничен') {
        return historyPhrase('labels.forbidden')
      }
      if (val === false || val === 'False' || val === 0 || val === '0' || val === 'Разрешён') {
        return historyPhrase('labels.allowed')
      }
      return val ?? ''
    },
  },
  Sites: {
    license_expiry_date: valueDate,
    manufacturer_country_certificate_expiry_date: valueDate,
    certificate_expiry_date: valueDate,
    doc_date: valueDate,
  },
  GmpStatusList: {
    site: valueRelation,
    status: (ch, change, rel, prop) => (
      isHistoryIdList(change[prop])
        ? valueConfigIdList('gmp_status_list.status')(ch, change, rel, prop)
        : valueRelation(ch, change, rel, prop)
    ),
    status_certificate: valueRelation,
    eaeu_country: valueRelation,
    date_of_documents_request: valueDate,
    planned_submission_date: valueDate,
    approval_of_submission_by_global_team: valueDate,
    actual_submission_date: valueDate,
    ha_notification: valueDate,
    planned_inspection_date: valueDateRange,
    inspections_dates: valueDateRange,
    inspection_report: valueDate,
    gmp: valueDate,
    existing_gmp_certificate_expiring_date: valueDate,
    restrictions: valueRelation,
    covered_dosage_form_stage: (ch, change, rel, prop) => (
      isHistoryIdList(change[prop])
        ? valueConfigIdListLocalized(
          'gmp_status_list.covered_dosage_form_stage',
          'gmp_status_list.covered_dosage_form_stage_ru',
        )(ch, change, rel, prop)
        : valueRelationConfigLocalized(
          'gmp_status_list.covered_dosage_form_stage',
          'gmp_status_list.covered_dosage_form_stage_ru',
        )(ch, change, rel, prop)
    ),
  },
  GmpForSubmission: {
    product: valueRelation,
    manual_bu: valueConfigId('gmp_for_submission.bu'),
    ra_sub_plan_in_ru: valueDate,
    bdp_site: valueRelation,
    bdp_site_gmp_certificate: valueConfigId('gmp_for_submission.bdp_site_gmp_certificate'),
    bdp_site_inspection_date: valueDate,
    batch_release_site: valueRelation,
    br_site_gmp_certificate: valueConfigId('gmp_for_submission.br_site_gmp_certificate'),
    br_site_inspection_date: valueDate,
  },
  GmpForSubmissionAction: {
    user: valueRelation,
    date: valueDate,
  },
  EaeuProductContacts: {
    country: valueRelation,
    bu: valueRelation,
    product: valueRelation,
    ra_cmc: valueRelation,
    ra_cmc_user: valueRelation,
    ra_cmc_manual: valueBool,
    ra_pil: valueRelation,
    ra_pil_user: valueRelation,
    ra_pil_manual: valueBool,
    ra_lead: valueRelation,
    ra_lead_user: valueRelation,
    ra_lead_manual: valueBool,
    medical_lead: valueRelation,
    medical_lead_user: valueRelation,
    medical_lead_manual: valueBool,
  },
  ProductManufacturers: {
    ...productModelValueFormatters.ProductManufacturers,
  },
  ProductDosageForms: {
    ...productModelValueFormatters.ProductDosageForms,
  },
  ProductDosageFormItems: {
    ...productModelValueFormatters.ProductDosageFormItems,
  },
  ProductAfsApis: {
    ...productModelValueFormatters.ProductAfsApis,
  },
  ProductAfsApiManufacturers: {
    ...productModelValueFormatters.ProductAfsApiManufacturers,
  },
  ProductIndications: {
    ...productModelValueFormatters.ProductIndications,
    status: valueRelation,
  },
  Submissions: {
    ...submissionModelValueFormatters.Submissions,
    stage: (_, change, __, prop) => submissionStageLabel(change[prop]),
  },
  Taxes: {
    ...taxesModelValueFormatters.Taxes,
  },
  ServiceNotes: {
    date_created: valueDate,
    doc_date: valueDate,
    author: valueRelation,
    product: valueRelation,
  },
  Templates: {
    doc_date: valueDate,
  },
  Medicines: {
    packing: valueConfigId('medicines.packing'),
    product_form: valueConfigId('medicines.product_form'),
    storage_conditions: valueConfigId('medicines.storage_conditions'),
    medicine_trademark: valueRelation,
    registration_at: valueDate,
    registration_certificates: valueRegistrationCertificates,
    ru_reissue_date: valueDate,
  },
  MedicineManufacturer: {
    prod_stage: valueConfigId('medicines.prod_stage'),
  },
  MedicineTrademarks: {
    medicinal_form: valueConfigId('medicine_trademarks.medicinal_form'),
  },
  Rsd: {
    product: valueRelation,
    registration_type: valueRsdEnum(RSD_REGISTRATION_TYPE_LABEL_KEYS),
    registration_procedure: valueRsdEnum(RSD_REGISTRATION_PROCEDURE_LABEL_KEYS),
    post_registration_measures: valueBool,
    post_registration_measures_type: valueRsdEnum(RSD_POST_REGISTRATION_MEASURE_TYPE_LABEL_KEYS),
    additional_requirements_status: valueRsdEnum(RSD_APPLICABLE_LABEL_KEYS),
    conditional_registration_status: valueRsdEnum(RSD_APPLICABLE_LABEL_KEYS),
    accelerated_registration_status: valueRsdEnum(RSD_APPLICABLE_LABEL_KEYS),
    exceptional_registration_status: valueRsdEnum(RSD_APPLICABLE_LABEL_KEYS),
    planned_submission_approval_date: valueDate,
    conclusion_date: valueDate,
    world_registration_period: valueDate,
    ref_state_registration_period: valueDate,
    early_access_program: valueRsdEnum(RSD_APPLICABLE_LABEL_KEYS),
    planned_rf_approval_date: valueDate,
    icsr_ready_date_if_applicable: valueDate,
    clinical_trial_permission_date: valueDate,
    icsr_ready_date: valueDate,
    fcsr_ready_date: valueDate,
    planned_first_global_submission_date: valueDate,
    planned_us_eu_approval_date: valueDate,
    planned_rf_submission_date: valueDate,
    localize_secondary_packaging: valueBool,
    localize_fdf_production: valueBool,
    localization_planned_dates: valueDate,
    localization_fdf_planned_dates: valueDate,
    circulation_preliminary_testing: valueBool,
    circulation_preliminary_testing_date: valueDate,
    circulation_launch_strategy: valueBool,
    circulation_launch_strategy_date: valueDate,
    patents: {
      default: valueRsdPatent,
      number: (_, change, __, prop) => change[prop] ?? '',
      expiry_date: valueDate,
    },
    deleted: valueBool,
  },
})

export function escapeHtmlAttr(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export function escapeHtmlText(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/** Подпись раздела документа (Taxes, Products и др.). */
export function entityDocumentDivisionLabel(division) {
  if (!division) {
    return historyPhrase('labels.document')
  }
  const key = String(division)
  const i18nKey = `history.uploads.${key}`
  if (i18n.global.te(i18nKey)) {
    return i18n.global.t(i18nKey)
  }
  if (key.startsWith('act_pp')) {
    return historyPhrase('labels.actPp')
  }
  return key || historyPhrase('labels.document')
}

/** @deprecated используйте entityDocumentDivisionLabel */
export const taxDocumentDivisionLabel = entityDocumentDivisionLabel

/** Метаданные upload из change и related.Uploads (как resolveUploadMeta в QEMS). */
export function resolveEntityUploadMeta(related, uploadId, metaFromChange) {
  const fromChange =
    metaFromChange && typeof metaFromChange === 'object' ? { ...metaFromChange } : {}
  const filename = String(
    fromChange.filename ?? fromChange.value ?? fromChange.old_value ?? ''
  ).trim()
  const secureFilename = String(
    fromChange.secure_filename ?? fromChange.old_secure_filename ?? ''
  ).trim()
  if (filename && secureFilename) {
    return { filename, secure_filename: secureFilename }
  }
  const id = uploadId ?? fromChange.upload_id ?? fromChange.old_upload_id
  if (id == null || id === '') {
    return filename ? { filename, secure_filename: secureFilename } : null
  }
  const numId = Number(id)
  if (!numId || Number.isNaN(numId)) {
    return filename ? { filename, secure_filename: secureFilename } : null
  }
  const bucket = related?.Uploads
  if (!bucket) {
    return filename ? { filename, secure_filename: secureFilename } : null
  }
  let obj = null
  if (Array.isArray(bucket)) {
    obj = bucket.find(d => Number(d?.id) === numId) || null
  } else if (typeof bucket === 'object') {
    obj = bucket[numId] ?? bucket[String(numId)] ?? null
  }
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj)
    } catch {
      return { filename: obj, secure_filename: secureFilename }
    }
  }
  if (!obj || typeof obj !== 'object') {
    return filename ? { filename, secure_filename: secureFilename } : null
  }
  return {
    filename: String(obj.filename ?? filename).trim(),
    secure_filename: String(obj.secure_filename ?? secureFilename).trim(),
  }
}

/** Имя файла со ссылкой на Docviewer по upload_id / change (как linkifyUploadIdHtml в QEMS). */
export function linkifyEntityUploadFromChange(related, uploadId, metaFromChange) {
  const meta = resolveEntityUploadMeta(related, uploadId, metaFromChange)
  if (!meta?.filename) return ''
  return linkifyEntityUploadHtml(meta.filename, meta.secure_filename)
}

/** @deprecated используйте linkifyEntityUploadFromChange */
export const linkifyTaxUploadFromChange = linkifyEntityUploadFromChange

/** @deprecated используйте resolveEntityUploadMeta */
export const resolveTaxUploadMeta = resolveEntityUploadMeta

/** Имя файла со ссылкой на Docviewer в истории (клик обрабатывает history/list.vue). */
export function linkifyEntityUploadHtml(filename, secureFilename) {
  const fn = String(filename ?? '').trim()
  const sec = String(secureFilename ?? '').trim()
  if (!fn) return ''
  if (!sec) return escapeHtmlText(fn)
  return `<a href="#" data-doc-sec="${escapeHtmlAttr(sec)}" data-doc-fn="${escapeHtmlAttr(
    fn
  )}" class="link-primary text-decoration-underline">${escapeHtmlText(fn)}</a>`
}

/** @deprecated используйте linkifyEntityUploadHtml */
export const linkifyTaxUploadHtml = linkifyEntityUploadHtml

const ENTITY_DOCUMENT_HISTORY_PREFIXES = [
  'Taxes:',
  'Products:',
  'ServiceNotes:',
  'Templates:',
  'Submissions:',
  'Sites:',
  'GmpStatusList:',
]

/** Запись истории относится к карточке с документами (Taxes, Products, ServiceNotes, Templates, Submissions, Sites). */
export function isEntityDocumentHistoryEntry(ch) {
  const key = String(ch?.hist_key || '')
  return ENTITY_DOCUMENT_HISTORY_PREFIXES.some(prefix => key.startsWith(prefix))
}

/** @deprecated используйте isEntityDocumentHistoryEntry */
export const isTaxesHistoryEntry = isEntityDocumentHistoryEntry

const ENTITY_DOCUMENT_ADDED_ACTIONS = new Set([
  'tax_document_added',
  'product_document_added',
  'service_note_document_added',
  'template_file_added',
  'submission_document_added',
  'site_document_added',
  'gmp_status_document_added',
])
const ENTITY_DOCUMENT_REPLACED_ACTIONS = new Set([
  'tax_document_replaced',
  'product_document_replaced',
  'service_note_document_replaced',
  'template_file_replaced',
  'submission_document_replaced',
  'site_document_replaced',
  'gmp_status_document_replaced',
])
const ENTITY_DOCUMENT_DELETED_ACTIONS = new Set([
  'submission_document_deleted',
  'service_note_document_deleted',
  'tax_document_deleted',
])

export function isEntityDocumentHistoryAction(action) {
  const act = String(action || '').trim()
  return (
    ENTITY_DOCUMENT_ADDED_ACTIONS.has(act) ||
    ENTITY_DOCUMENT_REPLACED_ACTIONS.has(act) ||
    ENTITY_DOCUMENT_DELETED_ACTIONS.has(act)
  )
}

export function isEntityDocumentAddedAction(action) {
  return ENTITY_DOCUMENT_ADDED_ACTIONS.has(String(action || '').trim())
}

export function isEntityDocumentDeletedAction(action) {
  return ENTITY_DOCUMENT_DELETED_ACTIONS.has(String(action || '').trim())
}

export function url(model, id) {
  if (!models[model]?.prefix) {
    return null
  }
  return `${models[model].historyPrefix || models[model].prefix}/${id}/history`
}

export function getVal(ch, change, rel, prop = 'value') {
  const v = values()
  const formatterPath = Array.isArray(change.path)
    ? change.path.filter(segment => !/^\d+$/.test(String(segment)))
    : change.path
  let res =
    (Array.isArray(formatterPath) && formatterPath.length
      ? _get(v, [ch.model_name, ...formatterPath]) || _get(v, formatterPath)
      : null) ||
    _get(v, [ch.model_name, ...change.path]) ||
    _get(v, change.path)
  if (res && typeof res === 'object' && !Array.isArray(res)) {
    const leaf = Array.isArray(formatterPath) ? formatterPath[formatterPath.length - 1] : null
    if (leaf && typeof res[leaf] === 'function') {
      res = res[leaf]
    } else if (typeof res.default === 'function') {
      res = res.default
    }
  }
  if (!res) {
    res = () => change[prop]
  }
  let val = res(ch, change, rel, prop)
  if (isRef(val) || isReactive(val)) {
    val = toValue(val)
  }
  return val ?? ''
}

/**
 * @param {string} pth
 * @returns {ChangeValueGetter}
 */
function valueConfigId(pth) {
  return (_, change, __, prop) => {
    const configStore = useConfigsStore()
    const v = configStore.getById(pth, normalizeRelationId(change[prop]), change[prop])
    return v
  }
}

function normalizeRelationId(raw) {
  if (raw && typeof raw === 'object' && raw.id !== undefined) {
    return raw.id
  }
  if (typeof raw !== 'string') {
    return raw
  }
  const trimmed = raw.trim()
  if (!trimmed) {
    return raw
  }
  if (/^\d+$/.test(trimmed)) {
    return Number(trimmed)
  }
  try {
    const parsed = JSON.parse(trimmed.replace(/'/g, '"'))
    if (parsed && typeof parsed === 'object' && parsed.id !== undefined) {
      return parsed.id
    }
  } catch {
    // keep raw value below
  }
  return raw
}

function valuePackBlocks(_, change, __, prop) {
  let raw = change[prop]
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      return raw
    }
  }
  if (!Array.isArray(raw)) {
    return ''
  }
  return raw
    .map(item => [item?.pack_size_en, item?.pack_size_ru, item?.shelf_life_ru, item?.storage_conditions_ru]
      .map(part => String(part || '').trim().replace(/\n/g, ', '))
      .filter(Boolean)
      .join(' / '))
    .filter(Boolean)
    .join('; ')
}

function valuePoEntries(_, change, __, prop) {
  let raw = change[prop]
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      return raw
    }
  }
  return normalizePoEntries(raw)
    .map(entry => formatPoLabel(entry))
    .filter(Boolean)
    .join(', ')
}

function isHistoryIdList(raw) {
  if (Array.isArray(raw)) {
    return true
  }
  return typeof raw === 'string' && raw.trim().startsWith('[')
}

function valueConfigIdList(pth) {
  // TODO: verify with func above
  return (_, change, __, prop) => {
    const configStore = useConfigsStore()
    const raw = normalizeIdList(change[prop])
    if (!Array.isArray(raw)) {
      return toValue(configStore.getById(pth, raw, raw)) ?? raw
    }
    return raw
      .map(id => toValue(configStore.getById(pth, id, id)))
      .filter(Boolean)
      .join(', ')
  }
}

/** Список id из EN-справочника; подписи — по языку UI. */
function valueConfigIdListLocalized(enPath, ruPath) {
  return (_, change, __, prop) => {
    const raw = normalizeIdList(change[prop])
    if (!Array.isArray(raw)) {
      return localizedConfigLabelById(enPath, ruPath, raw) ?? raw
    }
    return raw
      .map(id => localizedConfigLabelById(enPath, ruPath, id))
      .filter(Boolean)
      .join(', ')
  }
}

function normalizeIdList(raw) {
  if (Array.isArray(raw)) {
    return raw
  }
  if (typeof raw !== 'string') {
    return raw
  }
  const trimmed = raw.trim()
  if (!trimmed) {
    return []
  }
  try {
    const parsed = JSON.parse(trimmed.replace(/'/g, '"'))
    if (Array.isArray(parsed)) {
      return parsed
    }
  } catch {
    // Fallback for legacy values like [1, 2] or ['1', '2'].
  }
  return raw
}

/** Краткое показание на языке раскладки. Номер блока не используем: он сдвигается при удалении. */
function indicationShortTitle(ch, related) {
  const order = isEnglishHistoryLocale()
    ? ['short_text_en', 'short_text_ru']
    : ['short_text_ru', 'short_text_en']
  const changes = ch?.changes || []
  for (const field of order) {
    const change = changes.find(item => {
      const path = Array.isArray(item?.path) ? item.path : [item?.path]
      return String(path[path.length - 1] || '') === field
    })
    const text = String(change?.value || change?.old_value || '').trim()
    if (text && text !== 'None' && text !== 'null') {
      return text.length > 80 ? `${text.slice(0, 80)}…` : text
    }
  }
  const bucket = related?.ProductIndications
  const snap = bucket && ch?.model_id != null
    ? bucket[ch.model_id] ?? bucket[String(ch.model_id)]
    : null
  if (snap && typeof snap === 'object') {
    for (const field of order) {
      const text = String(snap[field] || '').trim()
      if (text) {
        return text.length > 80 ? `${text.slice(0, 80)}…` : text
      }
    }
  }
  return ''
}

/** «Показание {краткое}» — короткое RUS или ENG по текущей раскладке. */
function indicationHistoryEntityName(ch, related) {
  const label = historyPhrase('labels.indication')
  const short = indicationShortTitle(ch, related)
  return short ? `${label} ${short}` : label
}
